// src/pages/Profile/Profile.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { profileAPI } from '../../services/api';
import './Profile.css';
import { MdEdit } from "react-icons/md";

const MyProfile = () => {
  const location = useLocation();
  // const [loading, setLoading] = useState(false); // Changed to false initially
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Determine which region to show based on URL
  const [activeRegion, setActiveRegion] = useState(() => {
    if (location.pathname.endsWith('/pk')) return 'pk';
    return 'uk';
  });

  // Update region when URL changes
  useEffect(() => {
    if (location.pathname.endsWith('/pk')) {
      setActiveRegion('pk');
    } else {
      setActiveRegion('uk');
    }
  }, [location.pathname]);

  const [isEditing, setIsEditing] = useState(false);
  
  // Profile Data - will be populated from backend
  const [profile, setProfile] = useState({
    id: null,
    first_Name: '',
    last_Name: '',
    email: '',
    phone_Number: '',
    data_Joind: '',
    last_login: '',
    company_name: '',
    company_size: '',
    Vat_number: '',
    address: {
      city: '',
      area: '',
      street_Number: '',
      house_Number: ''
    },
    company_address: null,
    shop: null,
    type: ''
  });
  
  const [tempProfile, setTempProfile] = useState({});
  
  // Available shops and company addresses
  // const [shops, setShops] = useState([]);
  const [companyAddresses, setCompanyAddresses] = useState([]);

  // Cities for dropdown based on region
  const ukCities = ['London', 'Manchester', 'Birmingham', 'Liverpool', 'Leeds', 'Sheffield', 'Bristol', 'Newcastle', 'Nottingham', 'Edinburgh'];
  const pkCities = ['Karachi', 'Lahore', 'Islamabad', 'Rawalpindi', 'Faisalabad', 'Multan', 'Peshawar', 'Quetta', 'Sialkot', 'Gujranwala'];
  const cities = activeRegion === 'uk' ? ukCities : pkCities;

  // Load profile from backend
  useEffect(() => {
    fetchProfile();
    // fetchShops();
    fetchCompanyAddresses();
  }, []);

  const fetchProfile = async () => {
    // setLoading(true);
    setError(null);
    try {
      const response = await profileAPI.getProfile();
      const userData = response.data;
      
      // Format phone number for display (remove +92 if present)
      let displayPhone = userData.phone_Number || '';
      if (displayPhone.startsWith('+92') && displayPhone.length === 13) {
        displayPhone = '0' + displayPhone.substring(3);
      }
      
      setProfile({
        id: userData.id,
        first_Name: userData.first_Name || '',
        last_Name: userData.last_Name || '',
        email: userData.email || '',
        phone_Number: displayPhone,
        data_Joind: userData.data_Joind ? new Date(userData.data_Joind).toLocaleDateString() : 'N/A',
        last_login: userData.last_login ? new Date(userData.last_login).toLocaleDateString() : 'N/A',
        company_name: userData.company_name || '',
        company_size: userData.company_size || '',
        Vat_number: userData.Vat_number || '',
        address: userData.address || {
          city: '',
          area: '',
          street_Number: '',
          house_Number: ''
        },
        company_address: userData.company_address || null,
        shop: userData.shop || null,
        type: userData.type || 'CUSTOMER'
      });
      
      setTempProfile({
        first_Name: userData.first_Name || '',
        last_Name: userData.last_Name || '',
        email: userData.email || '',
        phone_Number: displayPhone,
        company_name: userData.company_name || '',
        company_size: userData.company_size || '',
        Vat_number: userData.Vat_number || '',
        address: { ...(userData.address || {
          city: '',
          area: '',
          street_Number: '',
          house_Number: ''
        }) },
        company_address: userData.company_address || null
      });
    } catch (err) {
      console.error('Error fetching profile:', err);
      if (err.response?.status === 404) {
        setError('Profile not found. Please contact support.');
      } else if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
      } else {
        setError('Failed to load profile. Please try again.');
      }
    } finally {
      // setLoading(false);
    }
  };

  // const fetchShops = async () => {
  //   try {
  //     const response = await profileAPI.getAllShops();
  //     setShops(response.data);
  //   } catch (err) {
  //     console.error('Error fetching shops:', err);
  //   }
  // };

  const fetchCompanyAddresses = async () => {
    try {
      const response = await profileAPI.getCompanyAddresses();
      setCompanyAddresses(response.data);
    } catch (err) {
      console.error('Error fetching company addresses:', err);
    }
  };

  const handleEdit = () => {
    setTempProfile({
      first_Name: profile.first_Name,
      last_Name: profile.last_Name,
      email: profile.email,
      phone_Number: profile.phone_Number,
      company_name: profile.company_name,
      company_size: profile.company_size,
      Vat_number: profile.Vat_number,
      address: { ...profile.address },
      company_address: profile.company_address
    });
    setIsEditing(true);
    setError(null);
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    // setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      // Prepare data for API - only include fields that can be updated
      const updateData = {
        first_Name: tempProfile.first_Name,
        last_Name: tempProfile.last_Name,
        email: tempProfile.email,
        phone_Number: tempProfile.phone_Number,
        company_name: tempProfile.company_name,
        company_size: tempProfile.company_size,
        Vat_number: tempProfile.Vat_number,
        address: {
          city: tempProfile.address.city || '',
          area: tempProfile.address.area || '',
          street_Number: tempProfile.address.street_Number || '',
          house_Number: tempProfile.address.house_Number || ''
        }
      };
      
      // Only add company_address if it exists
      if (tempProfile.company_address && tempProfile.company_address.address_Id) {
        updateData.company_address = tempProfile.company_address.address_Id;
      }
      
      const response = await profileAPI.updateProfile(updateData);
      
      // Update local state with response data
      const updatedUser = response.data.data;
      
      // Format phone number for display
      let displayPhone = updatedUser.phone_Number || '';
      if (displayPhone.startsWith('+92') && displayPhone.length === 13) {
        displayPhone = '0' + displayPhone.substring(3);
      }
      
      setProfile({
        ...profile,
        first_Name: updatedUser.first_Name,
        last_Name: updatedUser.last_Name,
        email: updatedUser.email,
        phone_Number: displayPhone,
        company_name: updatedUser.company_name,
        company_size: updatedUser.company_size,
        Vat_number: updatedUser.Vat_number,
        address: updatedUser.address || profile.address,
        company_address: updatedUser.company_address
      });
      
      setSuccessMessage('Profile updated successfully!');
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      
      // Parse error response
      if (err.response?.data) {
        const errorData = err.response.data;
        if (errorData.errors) {
          const errorMessages = Object.values(errorData.errors).flat();
          setError(errorMessages.join(', '));
        } else if (errorData.message) {
          setError(errorData.message);
        } else if (errorData.error) {
          setError(errorData.error);
        } else if (typeof errorData === 'string') {
          setError(errorData);
        } else {
          setError('Failed to update profile. Please check your inputs and try again.');
        }
      } else {
        setError('Failed to update profile. Please try again.');
      }
    } finally {
      // setLoading(false);
    }
  };

  const handleCancel = () => {
    setTempProfile({
      first_Name: profile.first_Name,
      last_Name: profile.last_Name,
      email: profile.email,
      phone_Number: profile.phone_Number,
      company_name: profile.company_name,
      company_size: profile.company_size,
      Vat_number: profile.Vat_number,
      address: { ...profile.address },
      company_address: profile.company_address
    });
    setIsEditing(false);
    setError(null);
    setSuccessMessage(null);
  };

  const handleChange = (field, value) => {
    setTempProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field, value) => {
    setTempProfile(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  };

  const handleCompanyAddressChange = (addressId) => {
    const selectedAddress = companyAddresses.find(addr => addr.address_Id === parseInt(addressId));
    setTempProfile(prev => ({
      ...prev,
      company_address: selectedAddress || null
    }));
  };

  const handleCitySelectChange = (e) => {
    const newValue = e.target.value;
    if (isEditing) {
      handleAddressChange('city', newValue);
    } else {
      setProfile(prev => ({
        ...prev,
        address: { ...prev.address, city: newValue }
      }));
    }
  };

  const getFullName = () => {
    return `${profile.first_Name} ${profile.last_Name}`.trim() || 'User';
  };

  const getUserInitials = () => {
    return `${profile.first_Name?.charAt(0) || ''}${profile.last_Name?.charAt(0) || ''}`.toUpperCase() || 'U';
  };

  const displayProfile = isEditing ? tempProfile : profile;

  // No initial loading spinner - show content immediately
  // const isOperationInProgress = loading;

  return (
    <div className="profile-main">
      <div className="profile-container">
        <div className="profile-header-wrapper">
          <div className="profile-header">
            <div className="profile-avatar">
              <div className="avatar-initials">{getUserInitials()}</div>
            </div>
            <div className="profile-title">
              <h2>{getFullName()}</h2>
              <span className="user-type-badge">{profile.type}</span>
            </div>
          </div>
          {!isEditing && (
             <button className="edit-profile-btn" onClick={handleEdit}>             <MdEdit /> Edit
            </button>
          )}
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError(null)} className="close-error">×</button>
          </div>
        )}
        
        {successMessage && (
          <div className="success-message">
            {successMessage}
            <button onClick={() => setSuccessMessage(null)} className="close-success">×</button>
          </div>
        )}

        <div className="user-id-section">
          <div className="user-id-label">Email:</div>
          <div className="user-id-value">
            {isEditing ? (
              <input
                type="email"
                value={displayProfile.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="Enter email"
                disabled={false}
              />
            ) : (
              <div className="user-id-text">{profile.email}</div>
            )}
          </div>
        </div>

        {/* Three Headers in One Line */}
        <div className="section-headers">
          <div className="section-header">Personal Information</div>
          <div className="section-header">Contact Information</div>
          <div className="section-header">Address</div>
        </div>

        <div className="rows-container">
          {/* First Row: First Name, Phone Number, House Number */}
          <div className="row-group">
            <div className="row-column">
              <div className="row-label">FIRST NAME</div>
              <div className="row-value">
                {isEditing ? (
                  <input
                    type="text"
                    value={displayProfile.first_Name}
                    onChange={(e) => handleChange('first_Name', e.target.value)}
                    disabled={false}
                  />
                ) : (
                  displayProfile.first_Name || 'N/A'
                )}
              </div>
            </div>
            <div className="row-column">
              <div className="row-label">PHONE NUMBER</div>
              <div className="row-value">
                {isEditing ? (
                  <input
                    type="tel"
                    value={displayProfile.phone_Number}
                    onChange={(e) => handleChange('phone_Number', e.target.value)}
                    placeholder="+923001234567 or 03001234567"
                    disabled={false}
                  />
                ) : (
                  displayProfile.phone_Number || 'N/A'
                )}
              </div>
            </div>
            <div className="row-column">
              <div className="row-label">HOUSE NUMBER</div>
              <div className="row-value">
                {isEditing ? (
                  <input
                    type="text"
                    value={displayProfile.address?.house_Number || ''}
                    onChange={(e) => handleAddressChange('house_Number', e.target.value)}
                    disabled={false}
                  />
                ) : (
                  displayProfile.address?.house_Number || 'N/A'
                )}
              </div>
            </div>
          </div>

          {/* Second Row: Last Name, Email, Street Number */}
          <div className="row-group">
            <div className="row-column">
              <div className="row-label">LAST NAME</div>
              <div className="row-value">
                {isEditing ? (
                  <input
                    type="text"
                    value={displayProfile.last_Name}
                    onChange={(e) => handleChange('last_Name', e.target.value)}
                    disabled={false}
                  />
                ) : (
                  displayProfile.last_Name || 'N/A'
                )}
              </div>
            </div>
            <div className="row-column">
              <div className="row-label">EMAIL</div>
              <div className="row-value">
                {isEditing ? (
                  <input
                    type="email"
                    value={displayProfile.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    disabled={false}
                  />
                ) : (
                  displayProfile.email || 'N/A'
                )}
              </div>
            </div>
            <div className="row-column">
              <div className="row-label">STREET NUMBER</div>
              <div className="row-value">
                {isEditing ? (
                  <input
                    type="text"
                    value={displayProfile.address?.street_Number || ''}
                    onChange={(e) => handleAddressChange('street_Number', e.target.value)}
                    disabled={false}
                  />
                ) : (
                  displayProfile.address?.street_Number || 'N/A'
                )}
              </div>
            </div>
          </div>

          {/* Third Row: Company Name, City Contact, Area */}
          <div className="row-group">
            <div className="row-column">
              <div className="row-label">COMPANY NAME</div>
              <div className="row-value">
                {isEditing ? (
                  <input
                    type="text"
                    value={displayProfile.company_name || ''}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    disabled={false}
                  />
                ) : (
                  displayProfile.company_name || 'N/A'
                )}
              </div>
            </div>
            <div className="row-column">
              <div className="row-label">CITY (CONTACT)</div>
              <div className="row-value">
                {isEditing ? (
                  <input
                    type="text"
                    value={displayProfile.address?.city || ''}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    disabled={false}
                  />
                ) : (
                  displayProfile.address?.city || 'N/A'
                )}
              </div>
            </div>
            <div className="row-column">
              <div className="row-label">AREA</div>
              <div className="row-value">
                {isEditing ? (
                  <input
                    type="text"
                    value={displayProfile.address?.area || ''}
                    onChange={(e) => handleAddressChange('area', e.target.value)}
                    disabled={false}
                  />
                ) : (
                  displayProfile.address?.area || 'N/A'
                )}
              </div>
            </div>
          </div>

          {/* Fourth Row: Company Size, Company Address, City with Select Dropdown */}
          <div className="row-group">
            <div className="row-column">
              <div className="row-label">COMPANY SIZE</div>
              <div className="row-value">
                {isEditing ? (
                  <select
                    value={displayProfile.company_size || ''}
                    onChange={(e) => handleChange('company_size', e.target.value)}
                    disabled={false}
                  >
                    <option value="">Select Size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="500+">500+ employees</option>
                  </select>
                ) : (
                  displayProfile.company_size || 'N/A'
                )}
              </div>
            </div>
            <div className="row-column">
              <div className="row-label">COMPANY ADDRESS</div>
              <div className="row-value">
                {isEditing ? (
                  <select
                    value={displayProfile.company_address?.address_Id || ''}
                    onChange={(e) => handleCompanyAddressChange(e.target.value)}
                    disabled={false}
                  >
                    <option value="">Select Company Address</option>
                    {companyAddresses.map(addr => (
                      <option key={addr.address_Id} value={addr.address_Id}>
                        {addr.city} - {addr.area} {addr.is_default ? '(Default)' : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  displayProfile.company_address 
                    ? `${displayProfile.company_address.city} - ${displayProfile.company_address.area}`
                    : 'N/A'
                )}
              </div>
            </div>
            <div className="row-column">
              <div className="row-label">CITY (ADDRESS)</div>
              <div className="row-value">
                <select
                  className="city-select"
                  value={displayProfile.address?.city || ''}
                  onChange={handleCitySelectChange}
                  disabled={!isEditing}                >
                  <option value="">Select City</option>
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Fifth Row: VAT Number, Account Created, Last Login */}
          <div className="row-group">
            <div className="row-column">
              <div className="row-label">VAT NUMBER</div>
              <div className="row-value">
                {isEditing ? (
                  <input
                    type="text"
                    value={displayProfile.Vat_number || ''}
                    onChange={(e) => handleChange('Vat_number', e.target.value)}
                    disabled={false}                  />
                ) : (
                  displayProfile.Vat_number || 'N/A'
                )}
              </div>
            </div>
            <div className="row-column">
              <div className="row-label">ACCOUNT CREATED</div>
              <div className="row-value">
                {profile.data_Joind}
              </div>
            </div>
            <div className="row-column">
              <div className="row-label">LAST LOGIN</div>
              <div className="row-value">
                {profile.last_login}
              </div>
            </div>
          </div>
        </div>

        {isEditing && (
          <div className="edit-actions">
            <button className="cancel-btn" onClick={handleCancel} disabled={false}>
              Cancel
            </button>
           <button className="save-btn" onClick={handleSave}>
               Save Changes
             </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProfile;